import { Injectable } from '@angular/core';
import {CanActivate, CanLoad, Router, UrlTree} from '@angular/router';
import {Observable, of, timeout} from 'rxjs';
import { ISessionService } from '@app/core/session';
import { AuthenticationService } from '@app/core/authentication/authentication.service';
import { IIdentityService } from '@app/core/providers';
import { IUserProfile } from '@app/core';
import { IdentityProvider } from '@app/core/providers/identity/identity-provider';
import { ContactUsService } from '@app/core/services/contact-us.service';
import {catchError, map, take} from "rxjs/operators";

@Injectable({
  providedIn: 'root'
})
export class AuthGuard implements CanActivate, CanLoad  {
  public constructor(
    private readonly authenticationService: AuthenticationService,
    sessionService: ISessionService,
    private readonly identityService: IIdentityService,
    private readonly identityProvider: IdentityProvider,
    private readonly router: Router,
    public contactUsService: ContactUsService
    ) {
      sessionService.init();
  }

  canSeeContactPhoneEverywhere(userInfo: IUserProfile): boolean {
    if (userInfo?.networkIds) {
      for (const network of userInfo?.networkIds) {
        if (
          ['EMEX', 'EMEXQ', 'VWRK', 'VWRKQ'].includes(network) ||
          network.indexOf('EMM') === 0 ||
          network.indexOf('VWM') === 0
        ) {
          return true;
        }
      }
    }
    return false;
  }

  public loadContactUsIfAvailable(): Observable<any> {
    return new Observable((observer) => {
      if (this.identityProvider?.contactUsList?.retailContactUsList && this.identityProvider?.contactUsList?.retailContactUsList.length > 0) {
        this.identityProvider.setContactUsList(this.identityProvider.contactUsList);
      }
      this.authenticationService.isSessionActive().subscribe((isAuthenticated: boolean) => {
        if (!isAuthenticated) {
          observer.next(false);
          observer.complete();
          return;
        }
        this.identityService.getUserProfile().subscribe((userInfoResponse: IUserProfile) => {
          if (this.canSeeContactPhoneEverywhere(userInfoResponse)) {
            this.contactUsService.getContactUsList().subscribe((data) => {
              if (data?.retailContactUsList || data?.retailContactUsList === undefined) {
                this.identityProvider.setContactUsList(data);
              }
            });
          }
          this.identityProvider.setCurrentUser({...this.authenticationService.user, userProfile: userInfoResponse});
        });
        observer.next(true);
        observer.complete();
      });
    });
  }

  canActivate(): Observable<boolean | UrlTree> {
    return this.authenticationService.isSessionActive().pipe(
      take(1),
      timeout(5000),
      catchError(()=> of(false)),
      map((isAuthenticated: boolean) => {
        if (!isAuthenticated) {
          return this.router.parseUrl('/sign-in');
        }
        this.loadContactUsIfAvailable().subscribe();
        return true;
    })
    );
  }

  canLoad(): Observable<boolean | UrlTree> {
    return this.canActivate();
  }
}
